import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { Link } from "react-router-dom";

/* ───────── アイコン ───────── */
export const Chevron = ({ className = "" }) => (
  <svg className={className} width="8" height="14" viewBox="0 0 8 14" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 1l6 6-6 6" />
  </svg>
);

export const BackChevron = ({ className = "" }) => (
  <svg className={className} width="12" height="20" viewBox="0 0 12 20" fill="none"
    stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 2L2 10l8 8" />
  </svg>
);

export const Check = ({ className = "" }) => (
  <svg className={className} width="18" height="18" viewBox="0 0 18 18" fill="none"
    stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3.5 9.5l3.5 3.5 7.5-8" />
  </svg>
);

export const InfoIcon = ({ className = "" }) => (
  <svg className={className} width="18" height="18" viewBox="0 0 18 18" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
    <circle cx="9" cy="9" r="7.5" />
    <path d="M9 8v5M9 5.2v.1" />
  </svg>
);

/* mailto: や tel: は同じタブで開く（空のタブが残らないように） */
export const externalProps = (href = "") =>
  /^(mailto|tel):/.test(href) ? {} : { target: "_blank", rel: "noopener noreferrer" };

/* ───────── ボタン ───────── */
const BUTTON_BASE =
  "inline-flex items-center justify-center gap-2 min-h-12 px-7 rounded-full text-[17px] font-semibold transition-colors select-none disabled:opacity-40 disabled:cursor-not-allowed";
const BUTTON_VARIANTS = {
  primary: "bg-tint text-white hover:bg-tint-dark",
  secondary: "bg-fill text-tint hover:bg-[#e8e8ed]",
  plain: "text-tint hover:text-tint-dark px-0 min-h-11",
};

export const buttonClass = (variant = "primary", extra = "") =>
  `${BUTTON_BASE} ${BUTTON_VARIANTS[variant]} ${extra}`;

/** to があれば内部リンク、href があれば外部リンク、それ以外は button */
export const Button = ({ variant = "primary", className = "", to, href, children, ...rest }) => {
  const cls = buttonClass(variant, className);
  if (to) return <Link to={to} className={cls} {...rest}>{children}</Link>;
  if (href)
    return (
      <a href={href} className={cls} {...externalProps(href)} {...rest}>
        {children}
      </a>
    );
  return <button type="button" className={cls} {...rest}>{children}</button>;
};

/* ───────── グループ化したリスト（設定アプリ風） ───────── */
export const GroupLabel = ({ children, className = "" }) => (
  <div className={`px-4 pb-1.5 text-[13px] text-ink-2 ${className}`}>{children}</div>
);

export const GroupFooter = ({ children }) => (
  <div className="px-4 pt-2 text-[13px] leading-relaxed text-ink-2">{children}</div>
);

export const Group = ({ children, className = "", tone = "white" }) => (
  <div
    className={`overflow-hidden rounded-[14px] ${tone === "white" ? "bg-white" : "bg-fill"} ${className}`}
  >
    {React.Children.toArray(children)
      .filter(Boolean)
      .map((child, i, arr) =>
        React.cloneElement(child, { _last: i === arr.length - 1 })
      )}
  </div>
);

/**
 * Row: 一行。to / href / onClick のどれかがあれば押せる行になる。
 *  - detail: 右側の薄い文字
 *  - accessory: "chevron" | "check" | "external" | ReactNode
 */
export const Row = ({
  children, sub, detail, accessory, to, href, onClick, tint = false,
  className = "", _last, ...rest
}) => {
  const inner = (
    <>
      <span className="min-w-0 flex-1">
        <span className={`block ${tint ? "text-tint" : ""}`}>{children}</span>
        {sub && <span className="mt-0.5 block text-[13px] leading-snug text-ink-2">{sub}</span>}
      </span>
      {detail != null && <span className="shrink-0 text-right text-ink-2">{detail}</span>}
      {accessory === "chevron" && <Chevron className="shrink-0 text-chevron" />}
      {accessory === "check" && <Check className="shrink-0 text-tint" />}
      {accessory === "external" && (
        <svg className="shrink-0 text-chevron" width="14" height="14" viewBox="0 0 14 14" fill="none"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
          <path d="M5 2h7v7M12 2L3 11" />
        </svg>
      )}
      {accessory && typeof accessory !== "string" && accessory}
    </>
  );
  const cls = `relative flex w-full items-center gap-3 min-h-[52px] py-3 pl-4 pr-4 text-left ${
    to || href || onClick ? "transition-colors hover:bg-black/[0.03] active:bg-black/[0.06]" : ""
  } ${className}`;
  const sep = !_last && (
    <span aria-hidden="true" className="pointer-events-none absolute bottom-0 left-4 right-0 h-px bg-line" />
  );
  if (to) return <Link to={to} className={cls} {...rest}>{inner}{sep}</Link>;
  if (href)
    return (
      <a href={href} className={cls} {...externalProps(href)} {...rest}>{inner}{sep}</a>
    );
  if (onClick)
    return <button type="button" onClick={onClick} className={cls} {...rest}>{inner}{sep}</button>;
  return <div className={cls} {...rest}>{inner}{sep}</div>;
};

/* ───────── 見出し ───────── */
export const SectionTitle = ({ title, lead, center = false, as: Tag = "h2", className = "" }) => (
  <div className={`${center ? "mx-auto text-center" : ""} max-w-2xl ${className}`}>
    <Tag className="text-[28px] font-bold leading-tight tracking-tight md:text-[40px]">{title}</Tag>
    {lead && <p className="mt-3 text-[17px] leading-relaxed text-ink-2">{lead}</p>}
  </div>
);

export const Container = ({ children, className = "", narrow = false }) => (
  <div className={`mx-auto w-full px-4 md:px-8 ${narrow ? "max-w-3xl" : "max-w-6xl"} ${className}`}>
    {children}
  </div>
);

/* ───────── トースト（alert の代わり） ───────── */
const ToastContext = createContext(() => {});

export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState(null);
  const timer = useRef(null);
  const show = useCallback((message) => {
    clearTimeout(timer.current);
    setToast({ message, key: Date.now() });
    timer.current = setTimeout(() => setToast(null), 2600);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-0 z-[100]">
        {toast && (
          <div
            key={toast.key}
            className="animate-toast fixed bottom-24 left-1/2 max-w-[90vw] -translate-x-1/2 rounded-full bg-ink/90 px-5 py-3 text-center text-[15px] text-white shadow-lg backdrop-blur"
          >
            {toast.message}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);

/* ───────── クリップボード ───────── */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    /* 下の方法で再挑戦 */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}
